// TypeScript i18n service
import {Translations} from '../types/components';

export class I18nService {
  private lang: string = 'en';
  private dict: Translations = {
    en: {
      sync: 'Sync',
      autoSync: 'Auto-Sync',
      timerView: 'Timer',
      settingsView: 'Settings',
      signOut: 'Sign out',
      langEN: 'English',
      langDE: 'German',
      // SignIn view
      signInTitle: 'Sign in',
      signInSubtitle: 'Enter your SNK Jira credentials to continue.',
      emailPlaceholder: 'you@snk.de',
      tokenPlaceholder: 'Your Jira API token',
      connect: 'Connect',
      connecting: 'Connecting…',
      snkOnlyNote: 'Note: Only @snk.de email addresses are allowed.',
      issueKey: 'Issue Key',
      description: 'Description',
      startTime: 'Start',
      endTime: 'End',
      startDateTime: 'Start Date/Time',
      addManualEntry: 'Add Manual Entry',
      cancel: 'Cancel',
      save: 'Save',
      date: 'Date',
      jiraConnection: 'Jira Connection',
      jiraEmail: 'Email',
      jiraToken: 'API Token',
      securityNotice: 'Your API token is stored locally in your browser. Keep it safe.',
      testConnection: 'Test Connection',
      generalSettings: 'General Settings',
      language: 'Language',
      lightMode: 'Light mode',
      dataManagement: 'Data Management',
      exportJson: 'Export JSON',
      importJson: 'Import JSON',
      start: 'Start',
      stop: 'Stop',
      restart: 'Restart',
      today: 'Today',
      yesterday: 'Yesterday',
      incompleteEntry: 'Incomplete entry',
      weeklyTotal: 'This week:',
      offline: 'You are offline. Changes will be queued.',
      online: 'Back online. You can sync now.',
      delete: 'Delete',
      pendingDeletion: "Pending deletion",
      edit: 'Edit',
      exitEditMode: 'Exit edit mode',
      confirm: 'Confirm',
      deleteConfirm: 'Delete?',
      signOutConfirm: 'Are you sure you want to sign out?',
      deleteFavoriteTitle: 'Delete favorite?',
      deleteFavoriteMessage: 'Are you sure you want to delete this favorite? This action cannot be undone.',
      deleteWorklogTitle: 'Delete worklog?',
      deleteWorklogMessage: 'Are you sure you want to delete this worklog entry? This action cannot be undone.',
      minutes: 'Min',
      error: 'Error',
      queued: 'Queued',
      local: 'Local',
      uploading: 'Uploading',
      synced: 'Synced',
      connectionSuccess: 'Connection success',
      connectionFailed: 'Connection failed',
      invalidIssueKey: 'Enter a valid issue key (e.g., PROJ-123).',
      invalidOrUnknownIssue: 'Invalid or unknown issue',
      required: 'This field is required.',
      invalidTimeRange: 'End time must be after start time.',
      durationTooLong: 'Duration cannot exceed 18 hours.',
      durationTooShort: 'Minimum duration is 1 minute.',
      descPlaceholder: 'What are you working on?',
      // Downloads View
      downloadFalcon: 'Download Falcon',
      pickPlatform: 'Pick your platform and get started in seconds.',
      released: 'Released',
      source: 'Source',
      loading: 'Loading…',
      noResults: 'No results',
      recent: 'Recent',
      resolvingLinks: 'Resolving links',
      pleaseWait: 'Please wait while we load the latest release info.',
      download: 'Download',
      otherOptions: 'Other options',
      recommended: 'Recommended',
      windows: 'Windows',
      windowsDesc: 'Compatible with Windows 10/11 (x64)',
      windowsExe: 'Download .exe',
      unavailable: 'Unavailable',
      msiInstaller: 'MSI Installer',
      macos: 'macOS',
      macosDesc: 'Compatible with macOS 12+ (Intel & Apple Silicon)',
      appleSiliconDmg: 'Apple Silicon .dmg',
      intelDmg: 'Intel .dmg',
      macosDmg: 'Download .dmg',
      macosZip: 'Download .zip',
      browser: 'Browser',
      browserExtension: 'Browser Extension',
      browserExtDesc: 'Enhance Falcon in your browser with a single click.',
      browserZip: 'Download .zip',
      downloads: 'Downloads',
      favoritesEmpty: 'What are you working on?',
      add: 'Add',
      name: 'Name',
      color: 'Color',
      nameOptional: 'Optional name',
      findExact: 'Find exact issue key',
      defaultWorklogDesc: 'Working on {issueKey}',
    },
    de: {
      sync: 'Sync',
      autoSync: 'Auto-Sync',
      timerView: 'Timer',
      settingsView: 'Einstellungen',
      signOut: 'Abmelden',
      langEN: 'Englisch',
      langDE: 'Deutsch',
      // SignIn view
      signInTitle: 'Anmelden',
      signInSubtitle: 'Geben Sie Ihre SNK Jira-Zugangsdaten ein, um fortzufahren.',
      emailPlaceholder: 'sie@snk.de',
      tokenPlaceholder: 'Ihr Jira API-Token',
      connect: 'Verbinden',
      connecting: 'Verbinden…',
      snkOnlyNote: 'Hinweis: Es sind nur E-Mail-Adressen mit @snk.de erlaubt.',
      issueKey: 'Issue Key',
      description: 'Beschreibung',
      startTime: 'Start',
      endTime: 'Ende',
      startDateTime: 'Startdatum/-zeit',
      addManualEntry: 'Manuellen Eintrag hinzufügen',
      cancel: 'Abbrechen',
      save: 'Speichern',
      date: 'Datum',
      jiraConnection: 'Jira-Verbindung',
      jiraEmail: 'E-Mail',
      jiraToken: 'API-Token',
      securityNotice: 'Ihr API-Token wird lokal im Browser gespeichert. Bitte sicher aufbewahren.',
      testConnection: 'Verbindung testen',
      generalSettings: 'Allgemeine Einstellungen',
      language: 'Sprache',
      lightMode: 'Heller Modus',
      dataManagement: 'Data Management',
      exportJson: 'JSON exportieren',
      importJson: 'JSON importieren',
      start: 'Start',
      stop: 'Stopp',
      restart: 'Neu starten',
      today: 'Heute',
      yesterday: 'Gestern',
      incompleteEntry: 'Unvollständiger Eintrag',
      weeklyTotal: 'Diese Woche:',
      offline: 'Sie sind offline. Änderungen werden in die Warteschlange gestellt.',
      online: 'Wieder online. Sie können jetzt synchronisieren.',
      delete: 'Löschen',
      pendingDeletion: "Ausstehende Löschung",
      edit: 'Bearbeiten',
      exitEditMode: 'Bearbeitungsmodus beenden',
      confirm: 'Bestätigen',
      deleteConfirm: 'Löschen?',
      signOutConfirm: 'Sind Sie sicher, dass Sie sich abmelden möchten?',
      deleteFavoriteTitle: 'Favorit löschen?',
      deleteFavoriteMessage: 'Sind Sie sicher, dass Sie diesen Favoriten löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteWorklogTitle: 'Arbeitszeiteintrag löschen?',
      deleteWorklogMessage: 'Sind Sie sicher, dass Sie diesen Arbeitszeiteintrag löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      minutes: 'Min',
      error: 'Fehler',
      queued: 'Wartend',
      local: 'Lokal',
      uploading: 'Hochladen',
      synced: 'Synchronisiert',
      connectionSuccess: 'Verbindung erfolgreich',
      connectionFailed: 'Verbindung fehlgeschlagen',
      invalidIssueKey: 'Gültigen Issue Key eingeben (z. B. PROJ-123).',
      invalidOrUnknownIssue: 'Ungültiger oder unbekannter Vorgang',
      required: 'Pflichtfeld.',
      invalidTimeRange: 'Endzeit muss nach der Startzeit liegen.',
      durationTooLong: 'Dauer darf 18 Stunden nicht überschreiten.',
      durationTooShort: 'Mindestdauer ist 1 Minute.',
      descPlaceholder: 'Woran arbeiten Sie?',
      // Downloads View
      downloadFalcon: 'Falcon herunterladen',
      pickPlatform: 'Wählen Sie Ihre Plattform und legen Sie in Sekunden los.',
      released: 'Veröffentlicht',
      source: 'Quelle',
      loading: 'Lädt…',
      noResults: 'Keine Ergebnisse',
      recent: 'Zuletzt verwendet',
      resolvingLinks: 'Links auflösen',
      pleaseWait: 'Bitte warten Sie, während wir die neuesten Release-Informationen laden.',
      download: 'Herunterladen',
      otherOptions: 'Weitere Optionen',
      recommended: 'Empfohlen',
      windows: 'Windows',
      windowsDesc: 'Kompatibel mit Windows 10/11 (x64)',
      windowsExe: '.exe herunterladen',
      unavailable: 'Nicht verfügbar',
      msiInstaller: 'MSI Installer',
      macos: 'macOS',
      macosDesc: 'Kompatibel mit macOS 12+ (Intel & Apple Silicon)',
      appleSiliconDmg: 'Apple Silicon .dmg',
      intelDmg: 'Intel .dmg',
      macosDmg: '.dmg herunterladen',
      macosZip: '.zip herunterladen',
      browser: 'Browser',
      browserExtension: 'Browser-Erweiterung',
      browserExtDesc: 'Verbessern Sie Falcon in Ihrem Browser mit einem einzigen Klick.',
      browserZip: '.zip herunterladen',
      downloads: 'Downloads',
      favoritesEmpty: 'Woran arbeiten Sie?',
      add: 'Hinzufügen',
      name: 'Name',
      color: 'Farbe',
      nameOptional: 'Optionaler Name',
      findExact: 'Exakten Issue Key suchen',
      defaultWorklogDesc: 'Arbeiten an {issueKey}',
      downloadExe: '.exe herunterladen',
      universalDmg: 'Universal .dmg',
    }
  };

  setLanguage(lang: string): void {
    this.lang = this.dict[lang] ? lang : 'en';
  }

  t(key: string): string {
    return (this.dict[this.lang] && this.dict[this.lang][key]) || key;
  }
}

// Create singleton instance
export const i18n = new I18nService();